import { HttpClient } from '@angular/common/http'
import { Component, OnInit, inject, computed } from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { DrawerItem, DrawerSelectEvent } from '@progress/kendo-angular-layout'
import { groupBy, GroupResult } from '@progress/kendo-data-query'
import { SVGIcon, menuIcon } from '@progress/kendo-svg-icons'
import { QuillModules } from 'ngx-quill'
import {
  combineLatest,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
} from 'rxjs'
import { v4 as uuidv4 } from 'uuid'

import {
  ContentRecord,
  DeepPartial,
  Enums,
  HuntPackageRecord,
  HuntTemplate,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import {
  categories,
  CCM_CATEGORIES,
  SupportedTools,
  ToolMapping,
  toolMappings,
} from '@cyborg-monorepo/hunter/matchboi'

import { getToolIcon } from '../../../../helpers/utils'
import {
  Child,
  MitreComponent,
  MITRETactics,
  processMitreEnterpriseJSON,
} from '../../../research/components/filter/components/dashboard/mitre/mitre.component'
import { SearchDataService } from '../../../research/services/search-data.service'
import {
  DEFAULT_SEARCH_STATE,
  TagDictionary,
} from '../../../research/services/search-state.service'
import { MitreTechniqueOption } from '../../../shared/components/mitre-multiselect/mitre-multiselect.component'
import { CustomHuntPackageService } from '../../../shared/services/custom-hunt-package.service'
import { HuntTemplateService } from '../../../shared/services/hunt-template.service'
import { UserService } from '../../../shared/services/user.service'

import { UpdatedHuntPackage } from './updated-hunt-package-interface'

let cachedMitreSubject: ReplaySubject<MITRETactics[]>

export interface StringTagDictionary {
  [key: string]: string[]
}

interface FormTool {
  toolType: SupportedTools
  deploymentSteps: string
  categories: string[]
  query: string
  queryTitle: string
}

@Component({
  standalone: false,
  selector: 'custom-hunt-package',
  templateUrl: './custom-hunt-package.component.html',
  styleUrls: ['custom-hunt-package.component.scss'],
})
export class CustomHuntPackageComponent implements OnInit {
  private searchDataService = inject(SearchDataService)
  private fb = inject(FormBuilder)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private huntTemplateService = inject(HuntTemplateService)
  private userService = inject(UserService)
  http = inject(HttpClient)
  httpClient = inject(HttpClient)
  customHuntPackageService = inject(CustomHuntPackageService)

  public categoryOptions = Object.values(Enums.HuntCategoryEnum).map(
    (value) => ({
      id: value,
      text: this.toLabel(value),
    }),
  )
  public priority = Enums.PriorityEnum
  public huntTemplate: HuntTemplate
  public huntDisabled = false
  public drawerWidth = 1000
  public tagSearch: TagDictionary
  public getToolIcon = getToolIcon
  public items: Array<DrawerItem> = [
    { text: 'Hunt Queries', selected: true },
    { text: 'Overview' },
    { text: 'Research' },
    { text: 'References' },
    { text: 'Response Actions' },
  ]
  public editorOptions: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['link', 'image'],
    ],
  }

  public menuSvg: SVGIcon = menuIcon
  public tags$: Observable<StringTagDictionary>
  public data: Array<ToolMapping> = toolMappings
  public categories: CCM_CATEGORIES = categories
  public categoryData: { category: string; name: string }[] = []

  public categoryKeys = Object.keys(categories)

  public groupedData: GroupResult[] = groupBy(this.data, [
    { field: 'category' },
  ]) as GroupResult[]

  public onSelect(ev: DrawerSelectEvent): void {
    this.selectedSection = ev.item.text
  }

  public contentId: string
  public selectedSection = 'Hunt Queries'
  public selectedThreatNames: string[] = []
  public form: FormGroup
  public expandedState = true
  public isEditing = false
  public huntTemplateId: string
  public user: Profile
  public tactics$: ReplaySubject<MITRETactics[]>
  public customContent: UpdatedHuntPackage
  public isNew = false
  public groupedMITRE: {
    techniqueName: string
    techniqueId: string
    tactic: string
  }[]

  public ngOnInit(): void {
    this.initForm()
    this.loadTags()
    this.addTool() // Add an initial tool

    for (const [key, value] of Object.entries(categories)) {
      this.categoryData.push({ category: key, name: value.name })
    }

    // Combine both route param observables
    combineLatest([
      this.route.params,
      this.route.parent?.parent?.params ?? of({}),
    ])
      .pipe(
        switchMap(([childParams, parentParams]) => {
          this.huntTemplateId = parentParams['huntTemplateId']
          this.isEditing = this.isNew = this.router.url.includes('/new')

          return this.isEditing
            ? of(null)
            : this.customHuntPackageService.getCustomContentByUUID(
                this.huntTemplateId,
                childParams.contentId,
              )
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.customContent = result
          this.populateForm(result)
        }

        this.addEmptyGeneralLink()
        this.addEmptyAnalysisLink()
        this.addEmptyBlogLink()
        this.addEmptyMalwareLink()
      })

    this.tactics$ =
      cachedMitreSubject ??
      (this.httpClient
        .get(MitreComponent.mitreEnterpriseJsonUrl)
        .pipe(map(processMitreEnterpriseJSON), shareReplay(1)) as ReplaySubject<
        MITRETactics[]
      >)

    cachedMitreSubject = this.tactics$

    this.tactics$.subscribe((tactics) => {
      this.groupedMITRE = this.flattenTactics(tactics)
    })

    this.userService.loadProfile().subscribe((user) => {
      this.user = user
    })
  }

  public flattenTactics(
    tactics: MITRETactics[],
  ): { techniqueName: string; techniqueId: string; tactic: string }[] {
    const result: {
      techniqueName: string
      techniqueId: string
      tactic: string
    }[] = []

    // Helper function to recursively flatten techniques
    function flattenChild(child: Child, tacticName: string): void {
      // Add the child (technique) to the result array
      result.push({
        techniqueName: child.name,
        techniqueId: child.external_references[0].external_id,
        tactic: tacticName,
      })

      // If the child has sub-techniques, recursively flatten them
      if (child.sub_techniques) {
        for (const subTechnique of child.sub_techniques) {
          flattenChild(subTechnique, tacticName)
        }
      }
    }

    // Loop through all tactics
    for (const tactic of tactics) {
      // Loop through each tactic's children (techniques)
      for (const child of tactic.children) {
        flattenChild(child, tactic.name)
      }
    }

    return result
  }

  // Separate method for form population
  private populateForm(content: HuntPackageRecord): void {
    // Create a new array that combines the data into an array of objects
    const mitreArray = content.mapping.mitre.mitre_attack_technique_ids.map(
      (techniqueId, index) => {
        return {
          techniqueId: techniqueId,
          techniqueName:
            content.mapping.mitre.mitre_attack_techniques[index] || '', // Handle case where ID might be missing
          tactic: content.mapping.mitre.mitre_attack_tactics[index] || '', // Handle case where tactic might be missing
        }
      },
    )
    this.form.patchValue({
      title: content.title,
      huntCategory: content.hunt_category,
      description: content.description ? content.description : '',
      threatNames: content.context.threat_names,
      actors: content.context.actors,
      diamondModels: [content.mapping.diamond_model],
      targetOses: content.context.target_oses,
      dependencies: content.dependencies,
      severity: content.severity,
      threatCategories: content.context.threat_categories,
      killChains: content.mapping.kill_chains,
      analystNotes:
        content.running_analyst_notes?.[0]?.current_analyst_note ?? '',
      threatDescription: content.context.threat_description,
      analystRunbook: content.response_actions.analyst_runbook,
      mitigationRecommendations: content.response_actions.analyst_runbook,
      mitre: mitreArray,
    })

    // Populate FormArrays
    this.setFormArray(content.references.general, 'general')
    this.setFormArray(content.references.blog_links, 'blogLinks')
    this.setFormArray(content.references.analysis, 'analysis')
    this.setFormArray(content.references.blog_links, 'blogLinks')
    this.setFormArray(content.references.malware_samples, 'malwareSamples')
    this.populateTools(content.content.records)
  }

  public onMITREChange(
    selectedIds: {
      techniqueId: string
      techniqueName: string
      tactic: string
    }[],
  ): void {
    this.form.get('mitre')?.setValue(selectedIds)
  }

  public editContent(): void {
    this.isEditing = true
  }

  private setFormArray(tags: string[], fieldName: string): void {
    const tagFormArray = this.form.get(fieldName) as FormArray
    if (tags)
      tags?.forEach((tag) => {
        tagFormArray.push(this.fb.control(tag)) // Create a FormControl for each tag
      })
  }

  public createTool(toolData?: ContentRecord): FormGroup {
    return this.fb.group({
      toolType: toolData?.tool,
      deploymentSteps: toolData?.deployment_requirements?.deployment_steps,
      categories: [toolData?.deployment_requirements?.categories], // Keeping this as is
      query: toolData?.content,
      queryTitle: toolData?.title,
    })
  }

  // Method to populate tools into the FormArray
  public populateTools(toolsData: ContentRecord[]): void {
    const toolsFormArray = this.form.get('tools') as FormArray
    toolsFormArray.clear()
    toolsData.forEach((toolData) => {
      try {
        const newTool = this.createTool(toolData)
        toolsFormArray.push(newTool)
      } catch (error) {
        console.error('Error creating tool:', error, 'Tool data:', toolData)
      }
    })
  }

  public closeDrawer(): void {
    this.isEditing = false
  }

  private initForm(): void {
    this.form = this.fb.group({
      title: ['', Validators.required],
      tools: this.fb.array([]),
      huntCategory: '',
      description: '',
      threatNames: [[]],
      actors: [[]],
      diamondModel: '',
      targetOses: [[]],
      dependencies: [[]],
      severity: '',
      threatCategories: [[]],
      killChains: [[]],
      mitre: [[]],
      analystNotes: '',
      threatDescription: '',
      general: this.fb.array([]),
      analysis: this.fb.array([]),
      blogLinks: this.fb.array([]),
      malwareSamples: this.fb.array([]),
      analystRunbook: '',
      mitigationRecommendations: '',
    })
  }

  private loadTags(): void {
    const tagParams: Params = this.searchDataService.stateToTagParams({
      ...DEFAULT_SEARCH_STATE,
      filtersDenormalized: [],
    })

    this.tags$ = this.searchDataService.getTagResults(tagParams).pipe(
      map((tagDictionary: TagDictionary) => {
        // Create new object with same keys but values mapped to string arrays
        return Object.fromEntries(
          Object.entries(tagDictionary).map(([key, tags]) => [
            key,
            tags.map((tag) => tag.key),
          ]),
        )
      }),
      take(1),
    )
  }

  public get toolsFormArray(): FormArray {
    return this.form.get('tools') as FormArray
  }

  public addTool(): void {
    const tool = this.fb.group({
      toolType: ['', Validators.required],
      deploymentSteps: '',
      categories: ['', Validators.required],
      query: ['', Validators.required],
      queryTitle: ['', Validators.required],
    })
    this.toolsFormArray.push(tool)
  }

  public removeTool(index: number): void {
    this.toolsFormArray.removeAt(index)
  }

  // General Links Methods

  public get generalLinksFormArray(): FormArray {
    return this.form.get('general') as FormArray
  }

  public addGeneralLink(link: string): void {
    if (link && link.trim()) {
      this.generalLinksFormArray.push(
        this.fb.control(link.trim(), [Validators.required]),
      )
    }
  }

  public removeGeneralLink(index: number): void {
    this.generalLinksFormArray.removeAt(index)
  }

  public canAddNewLink(): boolean {
    if (this.generalLinksFormArray.length === 0) return true
    const lastControl = this.generalLinksFormArray.at(
      this.generalLinksFormArray.length - 1,
    )
    return lastControl.value?.trim() !== ''
  }

  public addEmptyGeneralLink(): void {
    if (this.canAddNewLink()) {
      this.generalLinksFormArray.push(new FormControl(''))
    }
  }

  public toLabel(value: string): string {
    return value
      ?.replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (c) => c.toUpperCase()) // Capitalize first letters
  }

  // Analysis Links Methods

  public get analysisLinksFormArray(): FormArray {
    return this.form.get('analysis') as FormArray
  }

  public addAnalysisLink(link: string): void {
    if (link && link.trim()) {
      this.analysisLinksFormArray.push(
        this.fb.control(link.trim(), [Validators.required]),
      )
    }
  }

  public removeAnalysisLink(index: number): void {
    this.analysisLinksFormArray.removeAt(index)
  }

  public canAddNewAnalysisLink(): boolean {
    return this.canAddNewLinkToArray(this.analysisLinksFormArray)
  }

  public addEmptyAnalysisLink(): void {
    if (this.canAddNewAnalysisLink()) {
      this.analysisLinksFormArray.push(new FormControl(''))
    }
  }

  // Blog Links Methods

  public get linksFormArray(): FormArray {
    return this.form.get('blogLinks') as FormArray
  }

  public addLink(link: string): void {
    if (link && link.trim()) {
      this.linksFormArray.push(
        this.fb.control(link.trim(), [Validators.required]),
      )
    }
  }

  public removeLink(index: number): void {
    this.linksFormArray.removeAt(index)
  }

  public canAddNewBlogLink(): boolean {
    return this.canAddNewLinkToArray(this.linksFormArray)
  }

  public addEmptyBlogLink(): void {
    if (this.canAddNewBlogLink()) {
      this.linksFormArray.push(new FormControl(''))
    }
  }

  // Malware Links Methods

  public get malwareLinksFormArray(): FormArray {
    return this.form.get('malwareSamples') as FormArray
  }

  public addMalwareLink(link: string): void {
    if (link && link.trim()) {
      this.malwareLinksFormArray.push(
        this.fb.control(link.trim(), [Validators.required]),
      )
    }
  }

  public removeMalwareLink(index: number): void {
    this.malwareLinksFormArray.removeAt(index)
  }

  public canAddNewMalwareLink(): boolean {
    return this.canAddNewLinkToArray(this.malwareLinksFormArray)
  }

  public addEmptyMalwareLink(): void {
    if (this.canAddNewMalwareLink()) {
      this.malwareLinksFormArray.push(new FormControl(''))
    }
  }

  private canAddNewLinkToArray(formArray: FormArray): boolean {
    if (formArray.length === 0) return true
    const lastControl = formArray.at(formArray.length - 1)
    return lastControl.value?.trim() !== ''
  }
  public get uniqueTactics(): string {
    const uniqueTactics = Array.from(
      new Set(this.customContent.mapping?.mitre?.mitre_attack_tactics || []),
    )
    return uniqueTactics.join(', ')
  }

  public onSave(): void {
    this.isEditing = false
    const uuid = uuidv4()

    const customPackage: DeepPartial<UpdatedHuntPackage> = {
      title: this.form.value.title,
      hunt_category: this.form.value.huntCategory,
      description: this.form.value.description,
      UUID: this.isNew ? uuid : this.customContent.UUID, // UUID format
      community: true,
      author: `${this.user.firstName} ${this.user.lastName}`,
      created_date: new Date().toString(),
      last_updated: new Date().toString(),
      source: Enums.SourceEnum.CUSTOMER,
      source_origin: this.user.company.id as unknown as string,
      severity: this.form.value.severity,
      dependencies: this.form.value.dependencies,
      references: {
        general: this.form.value.general.filter(
          (link) => link && link.trim() !== '',
        ),
        deep_dives: [],
        analysis: this.form.value.analysis.filter(
          (link) => link && link.trim() !== '',
        ),
        blog_links: this.form.value.blogLinks.filter(
          (link) => link && link.trim() !== '',
        ),
        malware_samples: this.form.value.malwareSamples.filter(
          (link) => link && link.trim() !== '',
        ),
      },

      response_actions: {
        mitigation_recommendations: this.form.value.mitigationRecommendations,
        analyst_runbook: this.form.value.analystRunbook,
      },
      running_analyst_notes: this.form.value.analystNotes
        ? [
            {
              current_analyst_note: this.form.value.analystNotes,
              analyst_note_author: Enums.AnalystNoteAuthorEnum.CUSTOMER,
            },
          ]
        : [],
      context: {
        tooling: [],
        target_industries: [],
        target_countries: [],
        attack_surfaces: [],
        actors: this.form.value.actors,
        motivations: [],
        operations: [],
        target_os_versions: [],
        campaigns: [],
        source_regions: [],
        threat_names: this.form.value.threatNames,
        threat_description: this.form.value.threatDescription,
        threat_categories: this.form.value.threatCategories,
        target_regions: [],
        source_countries: [],
        target_oses: this.form.value.targetOses,
      },
      mapping: {
        kill_chains: this.form.value.killChains,
        diamond_model: this.form.value.diamondModel,
        mitre: {
          mitre_attack_tactics: this.form.value.mitre.map(
            (mitre: MitreTechniqueOption) => mitre.tactic,
          ),
          mitre_attack_techniques: this.form.value.mitre.map(
            (mitre: MitreTechniqueOption) => mitre.techniqueName,
          ),
          mitre_attack_technique_ids: this.form.value.mitre.map(
            (mitre: MitreTechniqueOption) => mitre.techniqueId,
          ),
        },
      },
      content: {
        records: this.form.value.tools.map((tool: FormTool) => ({
          goal: Enums.Goals.HUNT,
          tool: tool.toolType,
          deployment_requirements: {
            deployment_steps: tool.deploymentSteps,
            categories: tool.categories,
          },
          content: tool.query,
          title: tool.queryTitle,
        })),
        tools: this.form.value.tools.map((tool: FormTool) => tool.toolType),
      },
      tags: {
        tools: [],
        campaigns: [],
        platform_types: [],
        data_sources: [],
        goals: [Enums.Goals.HUNT],
        dependencies: this.form.value.dependencies,
        threat_names: this.form.value.threatNames,
        threat_categories: this.form.value.threatCategories,
        threat_types: [],
        attack_surfaces: [],
        target_oses: this.form.value.targetOses,
        actors: this.form.value.actors,
        tooling: [],
        diamond_models: [this.form.value.diamondModel],
        kill_chains: this.form.value.killChains,
        mitre_tactic_names: this.form.value.mitre.map(
          (mitre: MitreTechniqueOption) => mitre.tactic,
        ),
        mitre_technique_names: this.form.value.mitre.map(
          (mitre: MitreTechniqueOption) => mitre.techniqueName,
        ),
        mitre_technique_ids: this.form.value.mitre.map(
          (mitre: MitreTechniqueOption) => mitre.techniqueId,
        ),
        source_countries: [],
        source_regions: [],
        target_countries: [],
        target_regions: [],
        target_industries: [],
        exploit_or_vulns: [],
        motivations: [],
        severities: [this.form.value.severity],
      },
    }

    if (this.isNew) {
      this.huntTemplateService
        .addHuntPackagesToTemplate(this.huntTemplateId, [customPackage])
        .subscribe(() => {
          this.router.navigate(['../', uuid], {
            relativeTo: this.route,
          })
          this.customHuntPackageService.triggerRefresh()
        })
    } else {
      this.customHuntPackageService
        .updateCustomHuntPackage(this.huntTemplateId, customPackage)
        .subscribe((result) => {
          this.customContent = result
          this.customHuntPackageService.triggerRefresh()
        })
    }
  }

  public displayValue(value: string): string {
    return value == null || value === '' ? '--' : value
  }
}
